import { useEffect, useState } from 'react';
import { Modal, Button, Input } from 'components/ui';
import { ShopSelect } from 'components/ShopSelect';
import { useToast } from 'components/toast/ToastProvider';
import { useVendor } from 'features/vendor';
import { isValidPhone, normalizePhone } from 'lib/validators';
import { useAddStaff } from '../hooks/useStaff';

/** Dialog to add a staff member by name + phone, to a chosen shop. */
export function AddStaffModal({ isOpen, onClose }) {
  const { addStaff, loading } = useAddStaff();
  const { vendorId: activeVendorId } = useVendor();
  const toast = useToast();

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [shopId, setShopId] = useState(activeVendorId);
  const [errors, setErrors] = useState({});

  // Default the target shop to the active shop whenever the dialog opens.
  useEffect(() => {
    if (isOpen) setShopId(activeVendorId);
  }, [isOpen, activeVendorId]);

  const close = () => {
    setName('');
    setPhone('');
    setErrors({});
    onClose();
  };

  const handleSubmit = async () => {
    const nextErrors = {};
    if (!name.trim()) nextErrors.name = 'Enter a name.';
    if (!isValidPhone(phone)) nextErrors.phone = 'Enter a valid phone number.';
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    try {
      const result = await addStaff({
        vendorId: shopId || activeVendorId,
        name: name.trim(),
        phone: normalizePhone(phone),
      });
      if (result?.success) {
        toast.success('Staff added', `${name.trim()} can now help manage your shop.`);
        close();
      } else {
        toast.error('Could not add staff', result?.message || 'Please try again.');
      }
    } catch (err) {
      toast.error('Something went wrong', err.message);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      title="Add staff"
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={close} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} isLoading={loading}>
            Add staff
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <ShopSelect label="Add to shop" value={shopId} onChange={setShopId} />
        <Input
          label="Name"
          required
          autoFocus
          value={name}
          onChange={(event) => setName(event.target.value)}
          error={errors.name}
        />
        <Input
          label="Phone"
          required
          leftAddon="+91"
          type="tel"
          inputMode="numeric"
          value={phone}
          onChange={(event) => setPhone(event.target.value)}
          error={errors.phone}
        />
      </div>
    </Modal>
  );
}

export default AddStaffModal;
